/**
 * Cliente HTTP da API.
 *
 * - O access token vive só em memória (nunca em localStorage) para reduzir o impacto de XSS.
 * - O refresh token é um cookie httpOnly; em caso de 401 tentamos renovar uma única vez
 *   (requisições concorrentes compartilham a mesma renovação).
 */

import { LOCALE, t } from "../i18n";
import { apiOrigin, isApp, loadRefreshToken, saveRefreshToken, shareFile } from "../lib/platform";

export interface ApiErrorDetail {
  field: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details: ApiErrorDetail[] = [],
  ) {
    super(message);
  }

  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const d of this.details) if (d.field && !out[d.field]) out[d.field] = d.message;
    return out;
  }
}

/** Cabeçalhos comuns; no app, identifica o cliente para o servidor entregar o refresh token no corpo. */
function baseHeaders(): Record<string, string> {
  return isApp ? { "Accept-Language": LOCALE, "X-EAPace-Client": "app" } : { "Accept-Language": LOCALE };
}

/** Endereço completo de um caminho da API (no app, com o servidor escolhido). */
export function apiUrl(path: string): string {
  return `${apiOrigin()}/api/v1${path}`;
}

/** Guarda o refresh token devolvido no corpo (somente app). */
async function keepRefresh(body: { refresh_token?: string | null } | null | undefined) {
  if (isApp && body?.refresh_token) await saveRefreshToken(body.refresh_token);
}

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;
let onSessionLost: (() => void) | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function onSessionExpired(handler: () => void) {
  onSessionLost = handler;
}

async function parseError(res: Response): Promise<ApiError> {
  try {
    const body = await res.json();
    if (body?.error) {
      // senha provisória: o app troca para a tela de troca obrigatória (AuthContext ouve o evento)
      if (body.error.code === "password_change_required") window.dispatchEvent(new Event("eapace:password-change-required"));
      return new ApiError(res.status, body.error.code, body.error.message, body.error.details ?? []);
    }
  } catch {
    /* corpo não-JSON */
  }
  if (res.status >= 500) return new ApiError(res.status, "server_error", t("O servidor não respondeu como esperado. Tente novamente."));
  return new ApiError(res.status, "http_error", t("Erro {status}.", { status: res.status }));
}

export async function refreshAccessToken(): Promise<boolean> {
  if (!refreshing) {
    refreshing = (async () => {
      try {
        let init: RequestInit = { method: "POST", credentials: "same-origin", headers: baseHeaders() };
        if (isApp) {
          const stored = await loadRefreshToken();
          if (!stored) return false;
          init = { method: "POST", credentials: "omit", headers: { ...baseHeaders(), "Content-Type": "application/json" }, body: JSON.stringify({ refresh_token: stored }) };
        }
        const res = await fetch(apiUrl("/auth/refresh"), init);
        if (!res.ok) {
          accessToken = null;
          if (isApp && res.status === 401) await saveRefreshToken(null);
          return false;
        }
        const data = await res.json();
        accessToken = data.access_token;
        await keepRefresh(data);
        return true;
      } catch {
        return false;
      } finally {
        setTimeout(() => (refreshing = null), 0);
      }
    })();
  }
  return refreshing;
}

type Query = Record<string, string | number | boolean | null | undefined>;

export function withQuery(path: string, query?: Query): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== "" && v !== false) params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

export async function request<T>(method: string, path: string, body?: unknown, retry = true): Promise<T> {
  const headers: Record<string, string> = baseHeaders();
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  let res: Response;
  try {
    res = await fetch(apiUrl(path), {
      method,
      headers,
      credentials: isApp ? "omit" : "same-origin",
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, "network_error", t("Sem conexão com o servidor. Verifique sua internet."));
  }

  if (res.status === 401 && retry && !path.startsWith("/auth/login") && !path.startsWith("/auth/refresh")) {
    if (await refreshAccessToken()) return request<T>(method, path, body, false);
    onSessionLost?.();
  }
  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  const data = (await res.json()) as T;
  if (path.startsWith("/auth/login")) await keepRefresh(data as { refresh_token?: string | null });
  return data;
}

/** Encerra a sessão: no app envia o refresh token guardado no corpo e apaga o armazenamento. */
export async function logoutRequest(): Promise<void> {
  if (!isApp) return request<void>("POST", "/auth/logout", {});
  const stored = await loadRefreshToken();
  try {
    await request<void>("POST", "/auth/logout", { refresh_token: stored });
  } finally {
    await saveRefreshToken(null);
  }
}

/** Envio multipart (arquivos). Mesma renovação de sessão de `request`. */
export async function upload<T>(path: string, form: FormData, retry = true): Promise<T> {
  const headers: Record<string, string> = baseHeaders();
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  let res: Response;
  try {
    res = await fetch(apiUrl(path), { method: "POST", headers, credentials: isApp ? "omit" : "same-origin", body: form });
  } catch {
    throw new ApiError(0, "network_error", t("Sem conexão com o servidor. Verifique sua internet."));
  }
  if (res.status === 401 && retry) {
    if (await refreshAccessToken()) return upload<T>(path, form, false);
    onSessionLost?.();
  }
  if (res.status === 413 && !res.headers.get("content-type")?.includes("json")) {
    throw new ApiError(413, "file_too_large", t("O arquivo é grande demais para o servidor."));
  }
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as T;
}

/** Envio multipart com progresso de bytes (0–1). Mesma renovação de sessão de `request`. */
export function uploadWithProgress<T>(path: string, form: FormData, onProgress: (fraction: number) => void, retry = true): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", apiUrl(path));
    xhr.withCredentials = !isApp;
    for (const [k, v] of Object.entries(baseHeaders())) xhr.setRequestHeader(k, v);
    if (accessToken) xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onerror = () => reject(new ApiError(0, "network_error", t("Sem conexão com o servidor. Verifique sua internet.")));
    xhr.onload = async () => {
      if (xhr.status === 401 && retry) {
        if (await refreshAccessToken()) return resolve(uploadWithProgress<T>(path, form, onProgress, false));
        onSessionLost?.();
      }
      if (xhr.status >= 200 && xhr.status < 300) return resolve(JSON.parse(xhr.responseText) as T);
      if (xhr.status === 413) return reject(new ApiError(413, "file_too_large", t("O arquivo é grande demais para o servidor.")));
      try {
        const body = JSON.parse(xhr.responseText);
        if (body?.error) return reject(new ApiError(xhr.status, body.error.code, body.error.message, body.error.details ?? []));
        throw new Error(t("sem corpo de erro"));
      } catch {
        reject(new ApiError(xhr.status, "error", t("Não foi possível enviar o arquivo.")));
      }
    };
    xhr.send(form);
  });
}

/** Baixa um arquivo autenticado e oferece para salvar (o token não pode ir num link comum). */
export async function download(path: string, filename: string): Promise<void> {
  const go = () =>
    fetch(apiUrl(path), {
      headers: accessToken ? { ...baseHeaders(), Authorization: `Bearer ${accessToken}` } : baseHeaders(),
      credentials: isApp ? "omit" : "same-origin",
    });
  let res = await go();
  if (res.status === 401 && (await refreshAccessToken())) res = await go();
  if (!res.ok) throw await parseError(res);
  const blob = await res.blob();
  if (await shareFile(blob, filename).catch(() => false)) return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>("GET", withQuery(path, query)),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body ?? {}),
  patch: <T>(path: string, body: unknown) => request<T>("PATCH", path, body),
  put: <T>(path: string, body: unknown) => request<T>("PUT", path, body),
  delete: (path: string) => request<void>("DELETE", path),
};
